import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { SourcingLineStatus, UnitOfMeasure } from '../../enums';
import { BaseEntity } from './base.entity';
import { ItemEntity } from './item.entity';
import { SourcingFulfillmentAllocationEntity } from './sourcing-fulfillment-allocation.entity';
import { SourcingRequestEntity } from './sourcing-request.entity';
import { VendorEntity } from './vendor.entity';

@Index('idx_srl_sr', ['fkSourcingRequestId'], {})
@Index('idx_srl_item', ['fkItemId'], {})
@Index('idx_srl_vendor', ['fkVendorId'], {})
@Index('idx_srl_status', ['lineStatus'], {})
@Entity('sourcing_request_line')
export class SourcingRequestLineEntity extends BaseEntity {
	@Column('int', { name: '_fk_sourcing_request__id', unsigned: true })
	public fkSourcingRequestId!: number;

	@Column('int', { name: 'line_number', unsigned: true })
	public lineNumber!: number;

	@Column('text', { name: 'description' })
	public description!: string;

	@Column('int', { name: '_fk_item__id', nullable: true, unsigned: true })
	public fkItemId!: number | null;

	@Column('varchar', { name: 'item_external_id', length: 100, nullable: true })
	public itemExternalId!: string | null;

	@Column('decimal', {
		name: 'quantity_requested',
		precision: 15,
		scale: 4,
		default: 0,
	})
	public quantityRequested!: number;

	@Column('decimal', {
		name: 'quantity_on_order',
		precision: 15,
		scale: 4,
		default: 0,
	})
	public quantityOnOrder!: number;

	@Column('decimal', {
		name: 'quantity_received',
		precision: 15,
		scale: 4,
		default: 0,
	})
	public quantityReceived!: number;

	@Column('decimal', {
		name: 'quantity_fulfilled',
		precision: 15,
		scale: 4,
		default: 0,
	})
	public quantityFulfilled!: number;

	@Column('enum', {
		name: 'unit_of_measure',
		enum: UnitOfMeasure,
		nullable: true,
	})
	public unitOfMeasure!: UnitOfMeasure | null;

	@Column('enum', {
		name: 'line_status',
		enum: SourcingLineStatus,
		default: SourcingLineStatus.PENDING,
	})
	public lineStatus!: SourcingLineStatus;

	@Column('int', { name: '_fk_vendor__id', nullable: true, unsigned: true })
	public fkVendorId!: number | null;

	@Column('varchar', {
		name: 'vendor_item_number',
		length: 100,
		nullable: true,
	})
	public vendorItemNumber!: string | null;

	@Column('decimal', {
		name: 'estimated_cost',
		precision: 15,
		scale: 4,
		nullable: true,
	})
	public estimatedCost!: number | null;

	@Column('decimal', {
		name: 'actual_cost',
		precision: 15,
		scale: 4,
		nullable: true,
	})
	public actualCost!: number | null;

	@Column('date', { name: 'target_date', nullable: true })
	public targetDate!: Date | null;

	/**
	 * Free-text note captured when a buyer flips a line to
	 * `PENDING_INFO` — the question the requestor has to answer
	 * before purchasing can move forward (e.g. "manufacturer
	 * substitution OK?", "is this the right size?"). Cleared back to
	 * NULL when the line moves out of `PENDING_INFO`.
	 */
	@Column('text', { name: 'pending_info_note', nullable: true })
	public pendingInfoNote!: string | null;

	/**
	 * Timestamp of the most recent `PENDING_INFO` flip — used by the
	 * "Lines awaiting my response" widget to surface oldest-first
	 * (the request that's been sitting longest is the most urgent
	 * answer). Stays populated even after the line resolves so the
	 * audit trail survives without a dedicated history table.
	 */
	@Column('datetime', { name: 'pending_info_requested_at', nullable: true })
	public pendingInfoRequestedAt!: Date | null;

	@ManyToOne(() => SourcingRequestEntity, (request) => request.lines, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{ name: '_fk_sourcing_request__id', referencedColumnName: 'id' },
	])
	public fkSourcingRequest!: SourcingRequestEntity;

	@ManyToOne(() => ItemEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_item__id', referencedColumnName: 'id' }])
	public fkItem!: ItemEntity | null;

	@ManyToOne(() => VendorEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_vendor__id', referencedColumnName: 'id' }])
	public fkVendor!: VendorEntity | null;

	@OneToMany(
		() => SourcingFulfillmentAllocationEntity,
		(allocation) => allocation.fkSourcingRequestLine,
		{},
	)
	public fulfillmentAllocations!: Array<SourcingFulfillmentAllocationEntity>;
}
